import React, { useState, useEffect, useRef, useCallback } from 'react'
import Typography from '../../Typography'
import styles from './DownloadImages.module.sass'
import { useParams } from 'react-router-dom'
import Order from '../../../models/Order'
import PreviewImage from '../../PreviewImage'
import Button from '../../Button'
import Spinner from '../../Spinner'

const useOrder = (orderId, email) => {
  const [order, setOrder] = useState([])

  useEffect(() => {
    Order
      .includes({
        cart: {
          orderPackages: [
            'background',
            'package',
            'student',
            'studentImage',
            { sheets: ['pose', 'background', 'foreground'] },
            { addonSheets: ['pose', 'background'] },
            { api_option: 'image_types' },
            { extras: 'prices' },
          ],
        },
      })
      .where({ for_email: [orderId, email] })
      .first()
      .then((response) => setOrder(response.data))
  }, [orderId, email])

  return order;
};

const buildDownloadableImages = (order) => {
  const images = []
  if (!order || !order.cart || !order.cart.orderPackages) return images

  order.cart.orderPackages.forEach((orderPackage) => {
    if (!orderPackage.hasDownloadableImages) return

    if (orderPackage.sheets && orderPackage.sheets.length > 0) {
      orderPackage.sheets.filter((sheet) => sheet.hasDownloadableImage).forEach((sheet) => {
        images.push({
          type: 'pose',
          id: sheet.pose.id,
          imageUrl: sheet.pose.imageUrl,
          backgroundUrl: sheet.background && sheet.background.imageUrl,
          foregroundUrl: sheet.foregroundId ? sheet.foreground?.imageUrl : undefined,
          isPNG: sheet.pose.isPNG(),
          studentId: orderPackage.student.id,
          orderPackageId: orderPackage.id,
          backgroundId: sheet.background && sheet.background.id,
          foregroundId: sheet.foregroundId,
          itemKey: `pose-${sheet.pose.id}`,
        })
      })
    }

    if (orderPackage.addonSheets && orderPackage.addonSheets.length > 0) {
      orderPackage.addonSheets.filter((sheet) => sheet.hasDownloadableImage).forEach((sheet) => {
        images.push({
          type: 'pose',
          id: sheet.pose.id,
          imageUrl: sheet.pose.imageUrl,
          backgroundUrl: sheet.background && sheet.background.imageUrl,
          isPNG: sheet.pose.isPNG(),
          studentId: orderPackage.student.id,
          orderPackageId: orderPackage.id,
          backgroundId: sheet.background && sheet.background.id,
          itemKey: `pose-${sheet.pose.id}`,
        })
      })
    }

    if (orderPackage.studentImage && orderPackage.studentImage.imageUrl) {
      images.push({
        type: 'student_image',
        id: orderPackage.studentImage.id,
        imageUrl: orderPackage.studentImage.imageUrl,
        backgroundUrl: orderPackage.background && orderPackage.background.imageUrl,
        isPNG: orderPackage.studentImage.isPNG() && !!orderPackage.backgroundId,
        studentId: orderPackage.student.id,
        orderPackageId: orderPackage.id,
        backgroundId: orderPackage.background && orderPackage.background.id,
        itemKey: `student_image-${orderPackage.studentImage.id}`,
      })
    }
  })

  return images
}

const buildItemsList = (images) => {
  return images.map((img) => ({ type: img.type, id: img.id }))
}

const getDownloadUrl = (image, email) => {
  if (!image) return ''

  if (image.type === 'pose') {
    const baseUrl = image.isPNG ? '/download/download_png_image/' : '/download/download_image'
    return `${baseUrl}/${image.studentId}?pose_id=${image.id}&background_id=${image.backgroundId}&order_package_id=${image.orderPackageId}&email=${encodeURIComponent(email)}${image.foregroundId ? `&foreground_id=${image.foregroundId}` : ''}`
  } else {
    const baseUrl = image.isPNG ? '/download/download_png_image/' : '/download/download_image'
    return `${baseUrl}/${image.studentId}?student_image_id=${image.id}&background_id=${image.backgroundId}&order_package_id=${image.orderPackageId}&email=${encodeURIComponent(email)}`
  }
}

const DownloadImages = () => {
  const { order_id, email } = useParams();
  const order = useOrder(order_id, email);
  const [availability, setAvailability] = useState({})
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(true)
  const [currentIndex, setCurrentIndex] = useState(0)
  const pollingRef = useRef(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const elapsedTimerRef = useRef(null)
  const workOrderCreatedRef = useRef(false)

  const [downloadableImages, setDownloadableImages] = useState([])

  useEffect(() => {
    if (order && order.cart) {
      setDownloadableImages(buildDownloadableImages(order))
    }
  }, [order])

  const checkAvailability = useCallback((items) => {
    fetch('/download/check_image_availability', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_id, email, items }),
    })
      .then((res) => res.json())
      .then((data) => {
        setAvailability(data.availability || {})
        setIsCheckingAvailability(false)

        const allReady = Object.values(data.availability || {}).every((v) => v === true)
        if (allReady && pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
      })
      .catch(() => {
        setIsCheckingAvailability(false)
      })
  }, [order_id, email])

  const createWorkOrder = useCallback(() => {
    if (workOrderCreatedRef.current) return
    workOrderCreatedRef.current = true

    const unavailableItems = downloadableImages
      .filter((img) => availability[img.itemKey] !== true)
      .map((img) => ({ type: img.type, id: img.id }))

    const orderPackageIds = downloadableImages
      .filter((img) => availability[img.itemKey] !== true)
      .map((img) => img.orderPackageId)

    fetch('/download/create_download_work_order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        order_id,
        email,
        unavailable_items: unavailableItems,
        order_package_ids: orderPackageIds,
      }),
    }).catch(() => {})
  }, [order_id, email, downloadableImages, availability])

  useEffect(() => {
    if (downloadableImages.length === 0) return

    const items = buildItemsList(downloadableImages)
    checkAvailability(items)

    pollingRef.current = setInterval(() => {
      checkAvailability(items)
    }, 30000)

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [downloadableImages, checkAvailability])

  // Elapsed time tracker for unavailable images
  useEffect(() => {
    if (isCheckingAvailability || downloadableImages.length === 0) return

    const allReady = downloadableImages.length > 0 &&
      Object.values(availability).filter((v) => v === true).length === downloadableImages.length

    if (allReady) {
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current)
        elapsedTimerRef.current = null
      }
      setElapsedSeconds(0)
      return
    }

    if (!elapsedTimerRef.current) {
      elapsedTimerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1)
      }, 1000)
    }

    return () => {
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current)
        elapsedTimerRef.current = null
      }
    }
  }, [isCheckingAvailability, availability, downloadableImages])

  // Create work order at 60 seconds
  useEffect(() => {
    if (elapsedSeconds >= 60 && !workOrderCreatedRef.current) {
      createWorkOrder()
    }
  }, [elapsedSeconds, createWorkOrder])

  if (typeof order !== 'object') return null
  if (!order) return null

  const totalCount = downloadableImages.length
  const readyCount = Object.values(availability).filter((v) => v === true).length
  const allReady = totalCount > 0 && readyCount === totalCount

  const currentImage = downloadableImages[currentIndex]
  const isCurrentReady = currentImage ? availability[currentImage.itemKey] === true : false
  const downloadUrl = currentImage ? getDownloadUrl(currentImage, email) : ''

  const goToPrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1))
  }

  const goToNext = () => {
    setCurrentIndex((prev) => Math.min(totalCount - 1, prev + 1))
  }

  return (
    <div className={styles.container}>
      <Typography align="center" weight={500}>IMAGE DOWNLOAD</Typography>
      <Typography align="center" weight={600}>Order #{order.id}</Typography>

      {totalCount > 0 && (
        <div className={styles.progressContainer}>
          {isCheckingAvailability ? (
            <div className={styles.progressLabel}>Checking image availability...</div>
          ) : allReady ? (
            <div className={styles.allReady}>All images ready for download</div>
          ) : (
            <>
              <div className={styles.progressLabel}>{readyCount} of {totalCount} images ready</div>
              <div className={styles.progressTrack}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${(readyCount / totalCount) * 100}%` }}
                />
              </div>
            </>
          )}
        </div>
      )}

      <div className={styles.imageContainer}>
        {isCheckingAvailability && <div style={{ textAlign: 'center', padding: '20px' }}><Spinner /></div>}

        {!isCheckingAvailability && currentImage && (
          <>
            {totalCount > 1 && (
              <div className={styles.imageCounter}>
                Image {currentIndex + 1} of {totalCount}
              </div>
            )}

            <PreviewImage
              actualForegroundUrl={currentImage.foregroundUrl}
              foregroundUrl={currentImage.imageUrl}
              backgroundUrl={currentImage.backgroundUrl || ''}
              accessCodeOnly={false}
            />

            {isCurrentReady ? (
              <a style={{ display: 'block' }} href={downloadUrl}>
                <Button fullWidth variant="primary">
                  Download Image
                </Button>
              </a>
            ) : elapsedSeconds >= 60 ? (
              <div className={`${styles.statusBar} ${styles.statusDelayed}`}>
                Images Delayed - they will be emailed to you
              </div>
            ) : elapsedSeconds >= 30 ? (
              <div className={`${styles.statusBar} ${styles.statusWarning}`}>
                Images are still being prepared
              </div>
            ) : (
              <Button fullWidth variant="primary" disabled>
                Preparing Image...
              </Button>
            )}

            {totalCount > 1 && (
              <div className={styles.navButtons}>
                <button
                  className={styles.navButton}
                  onClick={goToPrevious}
                  disabled={currentIndex === 0}
                >
                  Previous
                </button>
                <button
                  className={styles.navButton}
                  onClick={goToNext}
                  disabled={currentIndex === totalCount - 1}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DownloadImages;
